import { useCallback } from "react";

import { useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { useFiltersConfigContext } from "app/pages/sales-intelligence/modules/advanced-search-filtering/context/FiltersConfigContext";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import ExcludeFilter from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/ExcludeFilter";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import {
    type LeadsSearchAllFiltersTypes,
    selectFiltersPanelValue,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { useFiltersUpdate } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useFiltersUpdate";
import type { LeadsSearchFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";
import type { ExcludeListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { ThunkDispatchCommon } from "single-spa/store/types";

type FilterUpdateEntry = {
    readonly key: keyof LeadsSearchAllFiltersTypes;
    readonly value: LeadsSearchAllFiltersTypes[keyof LeadsSearchAllFiltersTypes];
};

type HookValue = {
    handleFiltersUpdate: (listsToExclude: ReadonlyArray<ExcludeListType>) => void;
    isReady: boolean;
};

export const useModifiedExcludeFilters = (dispatch: ThunkDispatchCommon): HookValue => {
    const { filtersList, status } = useFiltersConfigContext<LeadsSearchFilterInstance>();

    const filtersPanelValue = useSelector(selectFiltersPanelValue);

    const handleFilterUpdate = useFiltersUpdate();

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: filtersPanelValue,
        filtersConfig: filtersList,
        onFiltersUpdate: handleFilterUpdate,
    });

    const handleFiltersUpdate: HookValue["handleFiltersUpdate"] = useCallback(
        (listsToExclude) => {
            if (status !== "ready" || listsToExclude.length === 0) {
                return;
            }

            try {
                const excludeSet = new Set(listsToExclude.map((c) => String(c)));
                const filters = filtersManager
                    .getAll()
                    .reduce(
                        (
                            acc: IFilterInstance<LeadsSearchAllFiltersTypes>[],
                            c: IFilterInstance<LeadsSearchAllFiltersTypes>,
                        ) => {
                            if (excludeSet.has(c.key)) {
                                acc.push(c);
                            }
                            return acc;
                        },
                        [],
                    );

                const filtersToUpdate = filters
                    .map((c): FilterUpdateEntry | null => {
                        if (c.filter instanceof ExcludeFilter) {
                            return {
                                key: c.key,
                                value: c.filter.getFullSelection(),
                            };
                        }

                        return null;
                    })
                    .filter((item): item is FilterUpdateEntry => item !== null);

                if (filtersToUpdate.length > 0) {
                    const updatePayload = Object.fromEntries(
                        filtersToUpdate.map(({ key, value }) => [key, value]),
                    ) as Partial<LeadsSearchAllFiltersTypes>;

                    filtersManager.updateFilters({ ...filtersPanelValue, ...updatePayload });
                }
            } catch (e) {
                Sentry.error(e.message, e);
            }
        },
        [dispatch, status, filtersManager],
    );

    return { handleFiltersUpdate, isReady: status === "ready" };
};
